/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { type ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import Chart from 'react-apexcharts';
import { pastelColorMap } from 'utils/helpers/general';
import './AreaChartTooltip.css';
import useAppTheme from 'hooks/useAppTheme';
import { convertStdDateString } from 'utils/helpers/dateTime';

const generateHTMLStringForTooltip = (
  dataObj?: Record<string, any>,
  darkMode?: boolean
) => {
  if (!dataObj) {
    return '';
  }
  let htmlString = '';
  for (const key in dataObj) {
    if (dataObj?.[key] === null || dataObj?.[key] === undefined) continue;
    htmlString += `<div class='tooltip-item' style="color: ${
      darkMode ? '#fff' : '#333'
    }">${key}: ${dataObj[key]}</div>`;
  }
  return htmlString;
};

export const ComponentsLineChart = ({
  data,
  height,
  darkBgColor,
}: {
  data: any[];
  height?: string;
  darkBgColor?: string;
}) => {
  // Get the current theme
  const { enableDarkTheme } = useAppTheme();

  // Format data for ApexCharts
  // const series = data.map((dt: any, index: number) => ({
  //   name: dt.name,
  //   data: dt.data.map((d: any) => ({
  //     x: dayjs.unix(d.timestamp).toDate(), // Convert timestamp to Date object
  //     y: d.total_cost_asset, // Y-axis value
  //     total_cost_asset: d.total_cost_asset, // Additional data for tooltip
  //     total_labour_cost: d.total_labour_cost, // Additional data for tooltip
  //     total_part_cost: d.total_part_cost, // Additional data for tooltip
  //     work_order_number: d.work_order_number, // Additional data for tooltip
  //     asset_id: d.asset_id, // Additional data for tooltip
  //     timestamp: d.timestamp, // Additional data for tooltip
  //   })),
  //   color: pastelColorMap[index] || generateRandomColor(), // Assign color
  // }));

  const aggregatedDataMap = data.reduce((acc: Record<string, any>, dt: any) => {
    dt.data.forEach((d: any) => {
      const dateKey = dayjs.unix(d.timestamp).format('YYYY-MM-DD'); // Group by date

      if (!acc[dateKey]) {
        acc[dateKey] = {
          x: dayjs.unix(d.timestamp).toDate(),
          y: d.total_cost_asset,
          total_cost_asset: d.total_cost_asset,
          total_labour_cost: d.total_labour_cost,
          total_part_cost: d.total_part_cost,
          work_order_number: new Set([d.work_order_number]),
          asset_id: new Set([d.asset_id]),
          timestamp: d.timestamp,
        };
      } else {
        acc[dateKey].y += d.total_cost_asset;
        acc[dateKey].total_cost_asset += d.total_cost_asset;
        acc[dateKey].total_labour_cost += d.total_labour_cost;
        acc[dateKey].total_part_cost += d.total_part_cost;
        acc[dateKey].work_order_number.add(d.work_order_number);
        acc[dateKey].asset_id.add(d.asset_id);
      }
    });

    return acc;
  }, {});

  const aggregatedSeries = [
    {
      name:
        data.length > 1
          ? 'Aggregated Data'
          : data.length === 0
          ? 'Asset Data'
          : data[0].name,
      data: Object.values(aggregatedDataMap)
        .map((item) => ({
          ...item,
          work_order_number: Array.from(item.work_order_number),
          asset_id: Array.from(item.asset_id),
        }))
        .sort((a, b) => a.timestamp - b.timestamp),
      color: pastelColorMap[0],
    },
  ];

  // ApexCharts options
  const options: ApexOptions = {
    markers: {
      size: 3,
    },
    chart: {
      type: 'area',
      stacked: false,
      zoom: {
        enabled: false, // disable zooming
        allowMouseWheelZoom: false,
      },
      toolbar: {
        show: false, // hide toolbar for zoom/reset
      },
      background: 'transparent', // Set chart background based on theme
    },
    dataLabels: {
      enabled: false, // Disable data labels for cleaner look
    },
    stroke: {
      curve: 'smooth', // Smooth lines
      colors: enableDarkTheme ? undefined : undefined, // Keep default stroke colors for both themes
    },
    xaxis: {
      type: 'datetime', // Use datetime for X-axis
      labels: {
        formatter: (value, timestamp) => {
          if (!timestamp) return '';
          return convertStdDateString(timestamp);
        },
        style: {
          colors: enableDarkTheme ? '#ffffff' : '#000000', // X-axis label color
        },
      },
      axisBorder: {
        color: enableDarkTheme ? '#ffffff' : '#000000', // X-axis border color
      },
      axisTicks: {
        color: enableDarkTheme ? '#ffffff' : '#000000', // X-axis tick color
      },
      tooltip: {
        enabled: false, // Disable default X-axis tooltip
      },
    },
    yaxis: {
      title: {
        text: 'Cost', // Y-axis label
        style: {
          color: enableDarkTheme ? '#ffffff' : '#000000', // Y-axis title color
        },
      },
      labels: {
        style: {
          colors: enableDarkTheme ? '#ffffff' : '#000000', // Y-axis label color
        },
        formatter: (value) => {
          if (typeof value === 'number') {
            return value.toFixed(2);
          }
          return value;
        },
      },
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }: any) => {
        const {
          total_cost_asset: cost,
          work_order_number: workOrderId,
          asset_id: assetId,
          timestamp,
        } = w.globals.initialSeries[seriesIndex].data[dataPointIndex];

        const seriesName = w.globals.initialSeries[seriesIndex]?.name;
        const seriesColor = w.globals.colors[seriesIndex];

        // assetInfo to be shown only when there is aggregated data in series name
        const assetInfo =
          seriesName === 'Aggregated Data'
            ? Array.isArray(assetId) && assetId?.length > 0
              ? ` (${Array.from(assetId).join(', ')})`
              : ''
            : '';

        return `
            <div class='apexcharts-custom-tooltip' style="background-color: ${
              enableDarkTheme ? '#1e1e1e' : '#fff'
            }; border: 1px solid ${enableDarkTheme ? '#444' : '#ddd'};">
              <div style="
                font-size: 12px;
                margin-bottom: 4px;
                text-transform: capitalize;
                color: ${seriesColor};
                font-weight: bold;
                white-space: normal;
                word-break: break-word;
              ">
                ${seriesName}${assetInfo ? ` ${assetInfo}` : ''}
              </div>
              ${generateHTMLStringForTooltip(
                {
                  Cost: cost,
                  'Work Order': workOrderId,
                  Date: timestamp ? convertStdDateString(timestamp * 1000) : '',
                },
                enableDarkTheme
              )}
            </div>
          `;
      },
    },
    legend: {
      show: true, // Show legend
      position: 'top', // Position legend at the top
      labels: {
        colors: enableDarkTheme ? '#ffffff' : '#000000', // Legend text color
      },
    },
    fill: {
      type: 'gradient', // Gradient fill for area
      gradient: {
        shadeIntensity: enableDarkTheme ? 1 : 0.8,
        opacityFrom: enableDarkTheme ? 0.7 : 0.6,
        opacityTo: enableDarkTheme ? 0.3 : 0.2,
        stops: [0, 100],
        inverseColors: false, // Keep colors consistent regardless of theme
      },
    },
    grid: {
      borderColor: enableDarkTheme ? '#555' : '#eee',
      strokeDashArray: 5,
      position: 'back',
      padding: {
        right: 8,
        left: 8,
      },
    },
    theme: {
      mode: enableDarkTheme ? 'dark' : 'light', // Set theme mode
    },
  };

  return (
    <Chart
      options={options}
      series={aggregatedSeries}
      type="area"
      height={height ?? '317.5'}
      width="100%"
    />
  );
};

export default ComponentsLineChart;
